import { Dialog, Transition } from "@headlessui/react";
import { Address } from "@helium/crypto";
import { CheckIcon, DownloadIcon } from "@heroicons/react/outline";
import { ExclamationIcon, XIcon } from "@heroicons/react/solid";
import { find, isEmpty, last, toLower, values } from "lodash";
import Papa from "papaparse";
import React, { Fragment, useCallback, useState } from "react";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import * as Yup from "yup";
import ExternalLink from "../../../components/ExternalLink";
import FileUpload from "../../../components/FileUpload";
import LoadingIcon from "../../../components/icons/LoadingIcon";
import { getColorFromId } from "../../../components/labels/utils";
import {
  ContactValues,
  useContacts
} from "../../../context/contacts/ContactsProvider";
import { downloadCsv } from "../../../utils/csv";
import { Contact, PaymentTypeKey } from "../../../utils/types";
import { CurrencyKey } from "../../../utils/useCurrency";
import {
  enhancedPaymentMethods,
  paymentTypesByCurrency
} from "./PaymentMethodFormSidebar";

interface ContactCsvRow extends Contact {
  name: string;
  email: string | null;
  phone: string | null;
  vat: string | null;
  currency: CurrencyKey;
  paypal_email: string | null;
  iban: string | null;
  bic: string | null;
  "First Name": string | null;
  "Last Name": string | null;
  "Phone Number": string | null;
  wallet_address: string | null;
  Email: string | null;
  schema_error?: string;
}

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  setErrorMessage: (message: string | null) => void;
}

const ImportContactsModal: React.FC<Props> = ({
  open,
  setOpen,
  setErrorMessage
}) => {
  const { addContacts, contacts } = useContacts();
  const [files, setFiles] = useState<File[]>([]);
  const [uploadWarnings, setUploadWarnings] = useState<string[]>([]);
  const [isImporting, setIsImporting] = useState(false);

  const emailExistAlready = useCallback(
    (newContacts: any, email: string) =>
      !!find(
        [...values(contacts), ...newContacts],
        (contact) => contact.email === email
      ),
    [contacts]
  );

  const importContacts = useCallback(async () => {
    if (isEmpty(files)) return;

    setIsImporting(true);
    setErrorMessage(null);
    setUploadWarnings([]);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      const extension = toLower(last(file.name.split(".")));

      const name = file.name.replace(`.${extension}`, "");

      let newContacts: ContactValues[] = [];
      let invalidContacts: ContactValues[] = [];

      switch (extension) {
        case "csv": {
          Papa.parse<ContactCsvRow>(file, {
            header: true,
            delimitersToGuess: [";", ","],
            trimHeaders: true,
            skipEmptyLines: true,
            complete: async (results) => {
              for (let i = 0; i < results.data.length; i++) {
                const contact = results.data[i];

                try {
                  const newContact: ContactValues = {
                    name:
                      contact.name ||
                      `${contact["First Name"]} ${contact["Last Name"]}`,
                    email: contact.email || contact.Email,
                    phone: contact.phone || contact["Phone Number"],
                    vat: contact.vat,
                    payment_methods: null
                  };

                  newContact.payment_methods = [];

                  await schema.validate(newContact);

                  if (
                    contact.currency &&
                    contact.wallet_address &&
                    paymentTypesByCurrency[contact.currency] &&
                    find(paymentTypesByCurrency[contact.currency], {
                      key: PaymentTypeKey.walletAddress
                    })
                  ) {
                    if (
                      contact.currency === CurrencyKey.hnt &&
                      !Address.isValid(contact.wallet_address)
                    ) {
                      throw Error("Invalid HNT wallet address");
                    }

                    newContact.payment_methods.push({
                      name: enhancedPaymentMethods[PaymentTypeKey.walletAddress]
                        .title,
                      type: PaymentTypeKey.walletAddress,
                      currency: contact.currency,
                      metadata: JSON.stringify({
                        walletAddress: contact.wallet_address
                      }),
                      primary: true
                    });
                  }

                  if (
                    contact.currency &&
                    contact.paypal_email &&
                    paymentTypesByCurrency[contact.currency] &&
                    find(paymentTypesByCurrency[contact.currency], {
                      key: PaymentTypeKey.paypal
                    })
                  ) {
                    newContact.payment_methods.push({
                      name: enhancedPaymentMethods[PaymentTypeKey.paypal].title,
                      type: PaymentTypeKey.paypal,
                      currency: contact.currency,
                      metadata: JSON.stringify({
                        email: contact.paypal_email
                      }),
                      primary: false
                    });
                  }

                  if (
                    contact.currency &&
                    contact.iban &&
                    paymentTypesByCurrency[contact.currency] &&
                    find(paymentTypesByCurrency[contact.currency], {
                      key: PaymentTypeKey.iban
                    })
                  ) {
                    newContact.payment_methods.push({
                      name: enhancedPaymentMethods[PaymentTypeKey.iban].title,
                      type: PaymentTypeKey.iban,
                      currency: contact.currency,
                      metadata: JSON.stringify({
                        iban: contact.iban,
                        bic: contact.bic
                      }),
                      primary: false
                    });
                  }

                  if (
                    newContact.email &&
                    emailExistAlready(newContacts, newContact.email)
                  ) {
                    throw Error("Duplicate contact by email address");
                  }

                  newContacts.push({
                    ...newContact
                  });
                } catch (error: any) {
                  contact.schema_error = error?.message ? error.message : "";

                  invalidContacts = [...invalidContacts, contact];
                }
              }

              if (invalidContacts.length > 0) {
                setErrorMessage(
                  "Some of the rows failed to be imported. The downloaded csv file contains the invalid lines along with an error message."
                );

                const contactsCsvContent = Papa.unparse(invalidContacts, {
                  delimiter: ";"
                });

                const a = document.createElement("a");

                let downloadLink = window.URL.createObjectURL(
                  new Blob([contactsCsvContent], {
                    type: "text/csv"
                  })
                );

                a.href = downloadLink;
                a.download = `${name}_invalid_rows.csv`;
                a.click();
              }
            }
          });

          break;
        }
      }

      setTimeout(async () => {
        if (newContacts.length > MAXIMUM_CONTACTS_PER_FILE) {
          setUploadWarnings((prevState) => [
            ...prevState,
            `The import is limited to ${MAXIMUM_CONTACTS_PER_FILE} entries, this has ${newContacts.length}`
          ]);

          setIsImporting(false);

          return;
        }

        if (!isEmpty(newContacts)) {
          await addContacts(newContacts, [
            { name, color: getColorFromId(name) }
          ]);
        }

        setIsImporting(false); // TODO: this should happen after the last file finished processing
        setOpen(false);
      }, 100);
    }
  }, [addContacts, emailExistAlready, files, setErrorMessage, setOpen]);

  const uploadWarnHandler = useCallback(
    (errs) => {
      setUploadWarnings(errs);
    },
    [setUploadWarnings]
  );

  const uploadChangeHandler = useCallback((files) => {
    setFiles(files);
  }, []);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed z-10 inset-0 overflow-y-auto"
        open={open}
        onClose={setOpen}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white dark:bg-gray-800 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400"
                  onClick={() => setOpen(false)}
                >
                  <span className="sr-only">Close</span>
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              <div className="mt-3 sm:mt-0">
                <Dialog.Title
                  as="h3"
                  className="text-lg leading-6 font-medium text-gray-900 dark:text-white"
                >
                  Import Contacts
                </Dialog.Title>

                <p className="mt-4 dark:text-gray-300">
                  Upload a <i>*.csv</i> file to import contacts.
                </p>

                <ul className="my-4 space-y-4">
                  <li className="flex space-x-3">
                    <CheckIcon
                      className="flex-shrink-0 h-5 w-5 text-green-500"
                      aria-hidden="true"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-300">
                      The <i>*.csv</i> file needs to be{" "}
                      <b>delimited by semicolon (;) or comma (,)</b>
                    </span>
                  </li>
                  <li className="flex space-x-3">
                    <CheckIcon
                      className="flex-shrink-0 h-5 w-5 text-green-500"
                      aria-hidden="true"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-300">
                      The first line can have the following (case-sensitive)
                      columns:
                      <strong className="ml-1 break-all">
                        name;email;phone;currency;paypal_email;iban;bic;wallet_address;vat
                      </strong>
                    </span>
                  </li>
                  <li className="flex space-x-3">
                    <CheckIcon
                      className="flex-shrink-0 h-5 w-5 text-green-500"
                      aria-hidden="true"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-300">
                      Only the <i>name</i> field is required. Phone numbers are
                      expected in the
                      <ExternalLink
                        className="inline-flex ml-2"
                        text="E.164 format"
                        url="https://www.cm.com/blog/how-to-format-international-telephone-numbers/"
                      />
                    </span>
                  </li>
                  <li className="flex space-x-3">
                    <CheckIcon
                      className="flex-shrink-0 h-5 w-5 text-green-500"
                      aria-hidden="true"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-300">
                      Contacts for which the email address already exists are
                      ignored when importing
                    </span>
                  </li>
                </ul>

                <div className="my-10">
                  <FileUpload
                    onChange={uploadChangeHandler}
                    onError={uploadWarnHandler}
                    limit={3}
                    limitSize={10}
                    extensions={["csv"]}
                  />
                </div>

                {!isEmpty(uploadWarnings) && (
                  <div className="mt-1">
                    <div className="rounded-md bg-yellow-50 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <ExclamationIcon
                            className="h-5 w-5 text-yellow-400"
                            aria-hidden="true"
                          />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-yellow-800">
                            Warning! Some files were not uploaded.
                          </h3>
                          <div className="mt-2 text-sm text-yellow-700">
                            {uploadWarnings.map((warn, index) => (
                              <p key={index}>{warn}</p>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-5 sm:mt-4 sm:flex justify-between">
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 sm:mt-0 sm:w-auto sm:text-sm dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400"
                  onClick={() => {
                    downloadCsv(
                      [
                        {
                          name: "John Doe",
                          email: "john@doe.com",
                          phone: "+351914749228",
                          hnt_address:
                            "13McBRN7t7Yx45hc1Uny4Sz8MgZYTJ7n2JLTZH2z4BrTxSLgVs5"
                        }
                      ],
                      "hotspotty-contacts-example"
                    );
                  }}
                >
                  <span>Download example file</span>
                  <DownloadIcon className="ml-2 h-4 w-4" aria-hidden="true" />
                </button>

                <div>
                  <button
                    type="button"
                    className="mt-3 sm:mt-0 w-full sm:w-auto inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 sm:text-sm dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400"
                    onClick={() => setOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="mt-3 sm:mt-0 sm:ml-4 w-full sm:w-auto inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 items-center disabled:opacity-50 disabled:cursor-default"
                    onClick={importContacts}
                    disabled={isEmpty(files) || isImporting}
                  >
                    {isImporting && (
                      <LoadingIcon className="h-4 w-4 animate-spin -ml-1 mr-3" />
                    )}
                    <span>{isImporting ? "Importing" : "Import"}</span>
                  </button>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ImportContactsModal;

//
// Utils
//

const MAXIMUM_CONTACTS_PER_FILE = 50;

const schema = Yup.object().shape({
  name: Yup.string()
    .min(3, "Please enter at least 3 characters")
    .required("Required"),
  email: Yup.string().email("Please enter a valid email address").nullable(),
  phone: Yup.string().test(
    "validPhone",
    "Please enter a valid phone number.",
    (value) => {
      if (!value) return true;

      return isPossiblePhoneNumber(`+${value.replaceAll("+", "")}`);
    }
  )
});
